import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCountries } from "../features/geo/hooks";
import SellerOnboardingAlert from "../components/SellerOnboardingAlert";
import TierBadge from "../components/TierBadge";
import { useMyTier } from "../features/subscriptions/useMyTier";
import { usersService } from "../features/users/service";
import { authService } from "../features/auth/service";
import {
  getMissingSellerOnboardingFields,
  getMissingSellerOnboardingFieldLabels,
  isSellerOnboardingComplete,
  isSellerRole,
  isBuyerRole,
  SELLER_ONBOARDING_PATH,
} from "../features/auth/onboarding";
import type { AuthUser } from "../features/auth/types";
import { useAuth } from "../context/AuthContext";
import { toPhoneVerificationChallenge, type PhoneVerificationChallenge } from "../features/auth/phoneVerification";
import { PhoneOtpConfirmPrompt } from "../components/PhoneOtpConfirmPrompt";
import type { ParsedApiError } from "../lib/http/apiErrors";
import { parseApiError } from "../lib/http/apiErrors";
import { recommendationsService } from "../features/recommendations/service";
import type { SellerScore } from "../features/recommendations/types";
import { onboardingFlowPublicService, type StepConfig, type FlowRole } from "../features/onboardingFlows/service";
import {
  FlowFieldRenderer,
  defaultFieldValue,
  isDynamicStepValid,
  phoneCountryCodeKey,
  type FlowFieldValue,
} from "../features/onboardingFlows/FieldRenderer";

// Seller types are admin/DB driven — fetched from /api/v1/onboarding/options
// (see sellerTypeOpts below). No hardcoded list here anymore.

interface BuyerPrefs {
  countries: string[];
  cities: string[];
  tags: string[];
  activities: string[];
  travel_style: string[];
  personality: string[];
  description: string;
  budget_min: number | null;
  budget_max: number | null;
}

interface InterestGraphData {
  destinations: Record<string, number>;
  tags: Record<string, number>;
  activities: Record<string, number>;
  categories?: Record<string, number>;
}

// ── Profile Photo Card — always visible, self-contained save ─────────────────

function ProfilePhotoCard({
  currentImageUrl,
  onSaved,
}: {
  currentImageUrl?: string;
  onSaved: () => void;
}) {
  const { refreshUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl,  setPreviewUrl]  = useState<string | null>(null);
  const [stagedFile,  setStagedFile]  = useState<File | null>(null);
  const [uploading,   setUploading]   = useState(false);
  const [removing,    setRemoving]    = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [successMsg,  setSuccessMsg]  = useState<string | null>(null);

  const displayImage = previewUrl || currentImageUrl;
  const busy = uploading || removing;

  // Selecting a file only stages it locally + previews it — nothing is
  // uploaded until the user explicitly clicks Save.
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setError(null);
    setSuccessMsg(null);

    const validation = usersService.validateImageFile(file);
    if (!validation.valid) { setError(validation.error || "Invalid image file"); return; }

    setStagedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const cancelStagedFile = () => {
    setStagedFile(null);
    setPreviewUrl(null);
    setError(null);
  };

  const handleSavePhoto = async () => {
    if (!stagedFile) return;
    setUploading(true);
    setError(null);
    try {
      await usersService.uploadProfileImage(stagedFile);
      await refreshUser();
      setStagedFile(null);
      setPreviewUrl(null);
      setSuccessMsg("Photo updated.");
      setTimeout(() => setSuccessMsg(null), 3000);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setSuccessMsg(null);
    setRemoving(true);
    try {
      await usersService.removeProfilePhoto();
      setPreviewUrl(null);
      await refreshUser();
      setSuccessMsg("Photo removed.");
      setTimeout(() => setSuccessMsg(null), 3000);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Remove failed");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex items-center gap-5">
      <div
        className="relative w-20 h-20 rounded-full border-2 border-gray-200 bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0 cursor-pointer hover:border-blue-400 transition-colors group"
        onClick={() => !busy && fileInputRef.current?.click()}
        title="Click to change photo"
      >
        {displayImage ? (
          <img src={displayImage} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className="flex flex-col gap-1.5">
        <div className="flex gap-2">
          {stagedFile ? (
            <>
              <button
                onClick={() => void handleSavePhoto()}
                disabled={busy}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-400 disabled:opacity-50 transition-all"
              >
                {uploading ? "Saving…" : "Save photo"}
              </button>
              <button
                onClick={cancelStagedFile}
                disabled={busy}
                className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-all"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-all"
              >
                {currentImageUrl ? "Change photo" : "Add photo"}
              </button>
              {currentImageUrl && (
                <button
                  onClick={handleRemove}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs bg-white border border-red-200 text-red-500 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-all"
                >
                  {removing ? "Removing…" : "Remove"}
                </button>
              )}
            </>
          )}
        </div>
        {error      && <p className="text-xs text-red-500">{error}</p>}
        {successMsg && <p className="text-xs text-emerald-600">{successMsg}</p>}
        <p className="text-xs text-gray-400">JPG, PNG, GIF, WebP · Max 5 MB</p>
      </div>
    </div>
  );
}

const steps = [
  { key: "countries",   title: "Where do you want to travel?",    subtitle: "Select your favourite destinations" },
  { key: "cities",      title: "Which cities interest you?",       subtitle: "Pick specific cities" },
  { key: "tags",        title: "What's your travel style?",        subtitle: "Choose travel styles" },
  { key: "activities",  title: "What do you want to do?",          subtitle: "Select activities" },
  { key: "travel_style",title: "How do you like to travel?",       subtitle: "Pick the travel styles that suit you" },
  { key: "personality", title: "What kind of traveller are you?",  subtitle: "Select personality traits and add a bio" },
  { key: "budget",      title: "What's your budget?",              subtitle: "Set budget range per person" },
];

interface PrefOpts {
  countries:    { value: string; label: string }[];
  cities:       { value: string; label: string }[];
  tags:         { value: string; label: string }[];
  activities:   { value: string; label: string; description?: string }[];
  travel_styles:{ value: string; label: string; description?: string }[];
  personalities:{ value: string; label: string; description?: string }[];
}

const EMPTY_PREF_OPTS: PrefOpts = { countries: [], cities: [], tags: [], activities: [], travel_styles: [], personalities: [] };

function PreferencesEditor({ initial, onSave, onCancel, initialStep = 0 }: {
  initial: BuyerPrefs;
  onSave: (prefs: BuyerPrefs) => void | Promise<void>;
  onCancel: () => void;
  initialStep?: number;
}) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [prefs, setPrefs] = useState<BuyerPrefs>(initial);
  const [loading, setLoading] = useState(false);
  const [stepSearch, setStepSearch] = useState("");
  const [opts, setOpts] = useState<PrefOpts>(EMPTY_PREF_OPTS);
  const [optsLoading, setOptsLoading] = useState(true);

  const { countries: geoCountries } = useCountries();

  useEffect(() => {
    import("axios").then(({ default: axios }) =>
      Promise.all([
        axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/onboarding/options?role=buyer`).then(({ data }) => data).catch(() => null),
        axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/onboarding/options?role=seller`).then(({ data }) => data).catch(() => null),
      ]).then(([b, s]) => {
        const toLabeled = (arr: any[]): { value: string; label: string; description?: string }[] =>
          (arr ?? []).map((x) => typeof x === "string" ? { value: x, label: x } : { value: x.value ?? x.label ?? "", label: x.label ?? x.value ?? "", description: x.description });
        setOpts({
          countries:     geoCountries.map(c => ({ value: c.name, label: c.name })),
          cities:        toLabeled(b?.cities       ?? s?.cities       ?? []),
          tags:          toLabeled(b?.tags         ?? []),
          activities:    toLabeled(b?.activities   ?? []),
          travel_styles: toLabeled(s?.travel_styles ?? []),
          personalities: toLabeled(s?.personalities ?? []),
        });
      }).finally(() => setOptsLoading(false))
    );
  }, [geoCountries]);

  const step = steps[currentStep];

  const handleSelect = (key: string, value: string) => {
    setPrefs((prev) => {
      const current = (prev as any)[key] as string[];
      const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [key]: updated };
    });
  };

  const goToStep = (n: number) => { setCurrentStep(n); setStepSearch(""); };

  const handleBudgetChange = (min: number | null, max: number | null) => {
    setPrefs((prev) => ({ ...prev, budget_min: min, budget_max: max }));
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      goToStep(currentStep + 1);
    } else {
      setLoading(true);
      try { await onSave(prefs); } finally { setLoading(false); }
    }
  };

  const canProceed = () => {
    switch (step.key) {
      case "countries":    return prefs.countries.length > 0;
      case "cities":       return prefs.cities.length > 0;
      case "tags":         return prefs.tags.length > 0;
      case "activities":   return prefs.activities.length > 0;
      case "travel_style": return prefs.travel_style.length > 0;
      case "personality":  return true;
      case "budget":       return prefs.budget_min !== null && prefs.budget_max !== null;
      default:             return true;
    }
  };

  const ChipRow = ({ items, selected, onToggle }: {
    items: { value: string; label: string; description?: string }[];
    selected: string[];
    onToggle: (v: string) => void;
  }) => (
    <div className="flex flex-wrap gap-1.5">
      {items.map((o) => (
        <button key={o.value} type="button" title={o.description} onClick={() => onToggle(o.value)}
          className={`px-3 py-1 rounded-full border text-xs font-medium transition-all ${
            selected.includes(o.value)
              ? "border-blue-500 bg-blue-500 text-white"
              : "border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300"
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );

  const CardGrid = ({ items, selected, onToggle }: {
    items: { value: string; label: string; description?: string }[];
    selected: string[];
    onToggle: (v: string) => void;
  }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-0.5">
      {items.map((o) => {
        const active = selected.includes(o.value);
        return (
          <button key={o.value} type="button" onClick={() => onToggle(o.value)}
            className={`text-left p-3 rounded-xl transition-all ${active ? "border-2 border-blue-500 bg-blue-50" : "border border-gray-200 bg-white hover:bg-gray-50"}`}>
            <div className={`text-xs font-semibold ${active ? "text-gray-900" : "text-gray-600"}`}>{o.label}</div>
            {o.description && <div className="text-xs text-gray-500 mt-0.5 leading-tight">{o.description}</div>}
          </button>
        );
      })}
    </div>
  );

  const searchBox = (placeholder: string) => (
    <input type="text" value={stepSearch} onChange={(e) => setStepSearch(e.target.value)}
      placeholder={placeholder}
      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 mb-2 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
    />
  );

  const SelectedChips = ({ items, onRemove, color }: { items: string[]; onRemove: (v: string) => void; color: string }) =>
    items.length > 0 ? (
      <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-100">
        {items.map((v) => (
          <button key={v} type="button" onClick={() => onRemove(v)}
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${color} hover:opacity-60 transition-all`}>
            {v} <span className="text-[10px]">✕</span>
          </button>
        ))}
      </div>
    ) : null;

  const FreeTextAdd = ({ onAdd, placeholder }: { onAdd: (v: string) => void; placeholder: string }) => {
    const [val, setVal] = useState("");
    return (
      <div className="flex gap-2 pt-1">
        <input type="text" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); if (val.trim()) { onAdd(val.trim()); setVal(""); } } }}
          placeholder={placeholder}
          className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
        />
        <button type="button" onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(""); } }}
          className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-all">Add</button>
      </div>
    );
  };

  if (optsLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex items-center gap-3">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-400/30 border-t-blue-500" />
        <span className="text-sm text-gray-500">Loading options…</span>
      </div>
    );
  }

  const renderStep = () => {
    switch (step.key) {
      case "countries": {
        const q = stepSearch.toLowerCase();
        const items = q ? opts.countries.filter((c) => c.label.toLowerCase().includes(q)) : opts.countries;
        return (
          <div className="space-y-2">
            {searchBox("Search countries…")}
            <div className="max-h-52 overflow-y-auto pr-0.5">
              <ChipRow items={items} selected={prefs.countries} onToggle={(v) => handleSelect("countries", v)} />
            </div>
            <SelectedChips items={prefs.countries} onRemove={(v) => handleSelect("countries", v)} color="bg-blue-50 text-blue-700 border-blue-200" />
            <FreeTextAdd placeholder="Add a custom country…" onAdd={(v) => { if (!prefs.countries.includes(v)) setPrefs((p) => ({ ...p, countries: [...p.countries, v] })); }} />
          </div>
        );
      }
      case "cities": {
        const q = stepSearch.toLowerCase();
        const items = q ? opts.cities.filter((c) => c.label.toLowerCase().includes(q)) : opts.cities;
        return (
          <div className="space-y-2">
            {searchBox("Search cities…")}
            <div className="max-h-52 overflow-y-auto pr-0.5">
              <ChipRow items={items} selected={prefs.cities} onToggle={(v) => handleSelect("cities", v)} />
            </div>
            <SelectedChips items={prefs.cities} onRemove={(v) => handleSelect("cities", v)} color="bg-indigo-50 text-indigo-700 border-indigo-200" />
            <FreeTextAdd placeholder="Add a custom city…" onAdd={(v) => { if (!prefs.cities.includes(v)) setPrefs((p) => ({ ...p, cities: [...p.cities, v] })); }} />
          </div>
        );
      }
      case "tags":
        return (
          <div className="space-y-2">
            <ChipRow items={opts.tags} selected={prefs.tags} onToggle={(v) => handleSelect("tags", v)} />
            <SelectedChips items={prefs.tags} onRemove={(v) => handleSelect("tags", v)} color="bg-purple-50 text-purple-700 border-purple-200" />
            <FreeTextAdd placeholder="Add a custom tag…" onAdd={(v) => { if (!prefs.tags.includes(v)) setPrefs((p) => ({ ...p, tags: [...p.tags, v] })); }} />
          </div>
        );
      case "activities": {
        const q = stepSearch.toLowerCase();
        const items = q ? opts.activities.filter((a) => a.label.toLowerCase().includes(q)) : opts.activities;
        return (
          <div className="space-y-2">
            {searchBox("Search activities…")}
            <CardGrid items={items} selected={prefs.activities} onToggle={(v) => handleSelect("activities", v)} />
            <SelectedChips items={prefs.activities} onRemove={(v) => handleSelect("activities", v)} color="bg-emerald-50 text-emerald-700 border-emerald-200" />
            <FreeTextAdd placeholder="Add a custom activity…" onAdd={(v) => { if (!prefs.activities.includes(v)) setPrefs((p) => ({ ...p, activities: [...p.activities, v] })); }} />
          </div>
        );
      }
      case "travel_style":
        return (
          <div className="space-y-2">
            <CardGrid items={opts.travel_styles} selected={prefs.travel_style} onToggle={(v) => handleSelect("travel_style", v)} />
            <SelectedChips items={prefs.travel_style} onRemove={(v) => handleSelect("travel_style", v)} color="bg-purple-50 text-purple-700 border-purple-200" />
            <FreeTextAdd placeholder="Add a custom style…" onAdd={(v) => { if (!prefs.travel_style.includes(v)) setPrefs((p) => ({ ...p, travel_style: [...p.travel_style, v] })); }} />
          </div>
        );
      case "personality":
        return (
          <div className="space-y-4">
            <CardGrid items={opts.personalities} selected={prefs.personality} onToggle={(v) => handleSelect("personality", v)} />
            <SelectedChips items={prefs.personality} onRemove={(v) => handleSelect("personality", v)} color="bg-indigo-50 text-indigo-700 border-indigo-200" />
            <FreeTextAdd placeholder="Add a custom trait…" onAdd={(v) => { if (!prefs.personality.includes(v)) setPrefs((p) => ({ ...p, personality: [...p.personality, v] })); }} />
            <div>
              <label className="text-gray-600 text-xs font-medium mb-1.5 block">About you <span className="font-normal text-gray-400">(optional)</span></label>
              <textarea
                value={prefs.description}
                onChange={(e) => setPrefs((p) => ({ ...p, description: e.target.value }))}
                rows={4}
                placeholder="Tell sellers about yourself — your travel style, group size, what you look for in a trip…"
                className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-none text-gray-900"
              />
              <p className="mt-1 text-xs text-gray-400">{(prefs.description ?? "").length}/500 characters</p>
            </div>
          </div>
        );
      case "budget":
        return (
          <div className="space-y-4">
            <div>
              <label className="text-gray-600 text-xs font-medium mb-1.5 block">Minimum Budget (&#8377;)</label>
              <input type="range" min="0" max="500000" step="5000"
                value={prefs.budget_min || 0}
                onChange={(e) => handleBudgetChange(Number(e.target.value), prefs.budget_max)}
                className="w-full accent-blue-500" />
              <div className="text-center font-medium text-gray-900 mt-1">&#8377;{(prefs.budget_min || 0).toLocaleString()}</div>
            </div>
            <div>
              <label className="text-gray-600 text-xs font-medium mb-1.5 block">Maximum Budget (&#8377;)</label>
              <input type="range" min="0" max="500000" step="5000"
                value={prefs.budget_max || 500000}
                onChange={(e) => handleBudgetChange(prefs.budget_min, Number(e.target.value))}
                className="w-full accent-blue-500" />
              <div className="text-center font-medium text-gray-900 mt-1">&#8377;{(prefs.budget_max || 500000).toLocaleString()}</div>
            </div>
            <div className="text-center text-gray-500 text-sm">
              Range: &#8377;{(prefs.budget_min || 0).toLocaleString()} &ndash; &#8377;{(prefs.budget_max || 500000).toLocaleString()}
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">Edit Buyer Preferences</h3>
        <div className="flex gap-2">
          <button onClick={onCancel} className="border border-gray-300 text-gray-600 hover:bg-gray-100 rounded-xl px-4 py-2 text-sm transition-all">Cancel</button>
          <button onClick={() => void handleNext()} disabled={!canProceed() || loading}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white rounded-xl px-6 py-2 text-sm font-semibold shadow-lg transition-all disabled:opacity-50">
            {loading ? "Saving..." : currentStep === steps.length - 1 ? "Save" : "Next"}
          </button>
        </div>
      </div>
      <div className="mb-5">
        <div className="flex gap-1 mb-1.5">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= currentStep ? "bg-blue-500" : "bg-gray-200"}`} />
          ))}
        </div>
        <div className="text-xs text-gray-500 text-center">Step {currentStep + 1} of {steps.length}</div>
      </div>
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">{step.title}</h4>
        <p className="text-xs text-gray-500 mt-0.5">{step.subtitle}</p>
      </div>
      <div className="mb-5">{renderStep()}</div>
      <div className="flex justify-between">
        <button onClick={() => goToStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0}
          className="border border-gray-300 text-gray-600 hover:bg-gray-100 rounded-xl px-4 py-2 text-sm transition-all disabled:opacity-30">
          Back
        </button>
        <button onClick={() => void handleNext()} disabled={!canProceed() || loading}
          className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white rounded-xl px-6 py-2 text-sm font-semibold shadow-lg transition-all disabled:opacity-50">
          {currentStep === steps.length - 1 ? "Save Preferences" : "Next"}
        </button>
      </div>
    </div>
  );
}

// ── Editable, tab-wise onboarding-data card ────────────────────────────────
// Business/contact fields no longer come back as top-level profile fields —
// they live in `profile.onboarding.steps[].data`, keyed per admin-configured
// field key. Rather than showing this data read-only and routing off to the
// separate onboarding wizard, we fetch the resolved flow (field schema) for
// the user's role/country, render one tab per onboarding step, and let the
// user edit fields inline (via the same FlowFieldRenderer the wizards use)
// and save individual steps without leaving the page.

function EditableOnboardingStepsCard({
  profile,
  role,
  title = "Profile Details",
  subtitle = "Filled in during onboarding — click Edit to update",
  activeStepKey,
  onActiveStepKeyChange,
  onSaved,
}: {
  profile: AuthUser;
  role: FlowRole;
  title?: string;
  subtitle?: string;
  /** Optional: controlled active tab (e.g. driven by the completeness card's "Fix" action). */
  activeStepKey?: string | null;
  onActiveStepKeyChange?: (key: string) => void;
  onSaved: () => void;
}) {
  const { refreshUser } = useAuth();
  const { countries: geoCountries } = useCountries();

  const [flowSteps, setFlowSteps] = useState<StepConfig[]>([]);
  const [flowLoading, setFlowLoading] = useState(true);

  const [internalTab, setInternalTab] = useState<string | null>(null);
  const tab = activeStepKey ?? internalTab;
  const setTab = (key: string) => { setInternalTab(key); onActiveStepKeyChange?.(key); };

  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, FlowFieldValue>>({});
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [phoneChallenge, setPhoneChallenge] = useState<PhoneVerificationChallenge | null>(null);

  const resolvedFlowIso2 = useMemo(() => {
    if (!geoCountries.length) return null;
    const found = geoCountries.find((c) => c.name === profile.country || c.iso2 === profile.country);
    return found?.iso2 ?? undefined;
  }, [geoCountries, profile.country]);

  useEffect(() => {
    if (resolvedFlowIso2 === null) return;
    setFlowLoading(true);
    onboardingFlowPublicService.getFlow(role, resolvedFlowIso2)
      .then((flow) => setFlowSteps(flow.steps))
      .catch(() => setFlowSteps([]))
      .finally(() => setFlowLoading(false));
  }, [role, resolvedFlowIso2]);

  const userSteps = profile.onboarding?.steps ?? [];
  // Tabs are driven by the full flow definition, not the user's partial step
  // records — a step the seller *skipped* (or never reached) still has a
  // flowSteps entry and must stay visible so it can be filled in later.
  // Filtering by `!s.skipped` on userSteps made skipped steps disappear
  // entirely, with no way to navigate back to them.
  const visibleSteps = flowSteps.length > 0 ? flowSteps : userSteps;

  // Default the active tab to the first visible step once steps are known.
  useEffect(() => {
    if (!tab && visibleSteps.length > 0) setTab(visibleSteps[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSteps.length]);

  const activeUserStep = userSteps.find((s) => s.key === tab) ?? null;
  const activeFlowStep = flowSteps.find((s) => s.key === tab) ?? null;

  const startEditing = () => {
    if (!activeFlowStep) return;
    const initial: Record<string, FlowFieldValue> = {};
    for (const f of activeFlowStep.fields) {
      let v = activeUserStep?.data?.[f.key];
      if (v === undefined && f.key === "offices") {
        v = (profile.offices ?? []).map((o: any) => ({
          ...o,
          country: Array.isArray(o.country) ? o.country[0] ?? "" : o.country ?? "",
          state:   Array.isArray(o.state)   ? o.state[0]   ?? "" : o.state   ?? "",
          city:    Array.isArray(o.city)    ? o.city[0]    ?? "" : o.city    ?? "",
        }));
      }
      if (v === undefined) {
        v = (profile as any)[f.key];
      }
      if (v === undefined && (profile as any).seller_preferences) {
        v = (profile as any).seller_preferences[f.key];
      }
      initial[f.key] = (v !== undefined && v !== null ? v : defaultFieldValue(f)) as FlowFieldValue;
      if (f.type === "phone") {
        const cc = activeUserStep?.data?.[phoneCountryCodeKey(f.key)];
        initial[phoneCountryCodeKey(f.key)] = (cc !== undefined && cc !== null ? cc : "") as FlowFieldValue;
      }
    }
    setValues(initial);
    setEditing(true);
    setStatusMsg(null);
    setFieldErrors({});
    setPhoneChallenge(null);
  };

  const cancelEditing = () => { setEditing(false); setStatusMsg(null); setFieldErrors({}); setPhoneChallenge(null); };

  const setField = (key: string, value: FlowFieldValue) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildPayload = (): Record<string, unknown> => {
    if (!activeFlowStep) return {};
    const payload: Record<string, unknown> = {};
    for (const f of activeFlowStep.fields) {
      const v = values[f.key];
      if (f.type === "phone") {
        if (typeof v === "string" && v.trim()) {
          payload[f.key] = v.trim();
          const cc = values[phoneCountryCodeKey(f.key)];
          if (typeof cc === "string" && cc.trim()) payload[phoneCountryCodeKey(f.key)] = cc.trim();
        }
      } else if (f.type === "number") {
        if (v !== "" && v !== undefined && !Number.isNaN(Number(v))) payload[f.key] = Number(v);
      } else if (Array.isArray(v)) {
        if (v.length) payload[f.key] = v;
      } else if (f.type === "checkbox") {
        payload[f.key] = v === true;
      } else if (f.type === "group") {
        if (v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length) payload[f.key] = v;
      } else if (typeof v === "string") {
        if (v.trim()) payload[f.key] = v.trim();
      }
    }
    return payload;
  };

  const handleSave = async () => {
    if (!activeFlowStep) return;
    setSaving(true);
    setStatusMsg(null);
    setFieldErrors({});
    setPhoneChallenge(null);
    try {
      await usersService.updateProfile({
        onboarding_steps: [{ key: activeFlowStep.key, completed: true, skipped: false, data: buildPayload() }],
      });
      await refreshUser();
      onSaved();
      setEditing(false);
      setStatusMsg("Saved.");
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) {
      // A changed phone/WhatsApp number holds the save pending OTP
      // confirmation rather than failing it — the server already parked
      // this step's payload, so stay in edit mode and surface the inline
      // code prompt instead of leaving the user stuck on a plain error.
      const challenge = toPhoneVerificationChallenge(err);
      if (challenge) {
        setPhoneChallenge(challenge);
        return;
      }
      const parsed = parseApiError(err, "Failed to save. Please try again.");
      setStatusMsg(parsed.message);
      if (parsed.fieldErrors?.length) {
        const byKey: Record<string, string> = {};
        for (const fe of parsed.fieldErrors) byKey[fe.key] = fe.message;
        setFieldErrors(byKey);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
      </div>

      {visibleSteps.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No details yet.</p>
      ) : (
        <>
          {/* Tab bar — one per onboarding step */}
          <div className="flex border-b border-gray-100 overflow-x-auto">
            {visibleSteps.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => { setTab(s.key); setEditing(false); }}
                className={`flex-shrink-0 px-3 py-2.5 text-sm font-medium border-b-2 transition-all ${
                  tab === s.key ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {statusMsg && (
            <p className={`text-sm px-4 py-2 rounded-xl ${statusMsg === "Saved." ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-600"}`}>
              {statusMsg}
            </p>
          )}

          {profile.onboarding?.pending_message && (activeFlowStep?.key === "destinations" || activeFlowStep?.key === "preferences" || activeFlowStep?.key === "market_preferences" || activeFlowStep?.key === "address" || activeFlowStep?.key === "location") && (
            <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-4 py-3 text-sm flex items-start gap-2 shadow-sm">
              <span className="mt-0.5 text-amber-500">⏳</span>
              <div>
                <p className="font-semibold mb-1">Pending Admin Verification</p>
                <p>{profile.onboarding.pending_message}</p>
              </div>
            </div>
          )}

          {flowLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-400/30 border-t-blue-500" />
              Loading fields&hellip;
            </div>
          ) : !activeFlowStep ? (
            <p className="text-xs text-gray-400 italic">Field definitions unavailable for this step.</p>
          ) : (
            <div className="pt-1">
              <div className="flex items-center justify-end mb-3">
                {!editing && (
                  <button
                    type="button"
                    onClick={startEditing}
                    className="bg-blue-500 hover:bg-blue-400 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all"
                  >
                    Edit
                  </button>
                )}
              </div>

              {editing ? (
                <div className="space-y-5">
                  {activeFlowStep.fields.map((f) => (
                    <div key={f.key} className={fieldErrors[f.key] ? "rounded-xl ring-1 ring-red-300 p-2 -m-2" : ""}>
                      <FlowFieldRenderer
                        field={f}
                        value={values[f.key] ?? defaultFieldValue(f)}
                        onChange={(v) => setField(f.key, v)}
                        stepFields={activeFlowStep.fields}
                        stepValues={values}
                        onChangeKey={(key, v) => setField(key, v)}
                        pendingValues={[
                          ...(profile.onboarding?.pending_market_review || []),
                          ...(profile.onboarding?.pending_market_review_names || []),
                        ]}
                      />
                      {fieldErrors[f.key] && (
                        <p className="text-xs text-red-600 mt-1">{fieldErrors[f.key]}</p>
                      )}
                    </div>
                  ))}
                  {phoneChallenge && (
                    <PhoneOtpConfirmPrompt
                      challenge={phoneChallenge}
                      onVerified={async () => {
                        await refreshUser();
                        setPhoneChallenge(null);
                        onSaved();
                        setEditing(false);
                        setStatusMsg("Saved.");
                        setTimeout(() => setStatusMsg(null), 3000);
                      }}
                      onCancel={() => setPhoneChallenge(null)}
                    />
                  )}
                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => void handleSave()}
                      disabled={saving || !isDynamicStepValid(activeFlowStep.fields, values)}
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white rounded-xl px-6 py-2 text-sm font-semibold shadow-lg transition-all disabled:opacity-50"
                    >
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="border border-gray-300 text-gray-600 hover:bg-gray-100 rounded-xl px-4 py-2 text-sm transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {activeFlowStep.fields.map((f) => {
                    const raw = activeUserStep?.data?.[f.key];
                    let displayVals: Record<string, FlowFieldValue>;
                    let isEmpty: boolean;
                    if (f.type === "phone") {
                      const ccRaw = activeUserStep?.data?.[phoneCountryCodeKey(f.key)];
                      displayVals = {
                        [f.key]: (raw as FlowFieldValue) ?? "",
                        [phoneCountryCodeKey(f.key)]: (ccRaw as FlowFieldValue) ?? "",
                      };
                      isEmpty = (raw === undefined || raw === null || raw === "") && (ccRaw === undefined || ccRaw === null || ccRaw === "");
                    } else {
                      displayVals = raw !== undefined && raw !== null
                        ? { [f.key]: raw as FlowFieldValue }
                        : { [f.key]: defaultFieldValue(f) };
                      isEmpty = raw === undefined || raw === null || raw === "" || (Array.isArray(raw) && raw.length === 0);
                    }
                    return (
                      <div key={f.key}>
                        <span className="text-gray-400 text-xs">{f.label}</span>
                        <div className={`mt-0.5 pointer-events-none ${isEmpty ? "opacity-60" : "opacity-90"}`}>
                          <FlowFieldRenderer
                            field={{ ...f, label: "" }}
                            value={displayVals[f.key]}
                            onChange={() => {}}
                            stepFields={activeFlowStep.fields}
                            stepValues={(activeUserStep?.data as Record<string, FlowFieldValue>) || {}}
                            pendingValues={[
                              ...(profile.onboarding?.pending_market_review || []),
                              ...(profile.onboarding?.pending_market_review_names || []),
                            ]}
                          />
                        </div>
                        {isEmpty && (
                          <p className="text-xs text-gray-400 italic mt-0.5">Not set</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ── Buyer basic info section ──────────────────────────────────────────────────

function BuyerBasicInfoSection({
  profile,
  onSaved,
  onboardingStepKey,
  onOnboardingStepKeyChange,
}: {
  profile: AuthUser;
  onSaved: () => void;
  onboardingStepKey?: string | null;
  onOnboardingStepKeyChange?: (key: string) => void;
}) {
  const { refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: profile.name ?? "",
    bio: profile.bio ?? "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatusMsg(null);
    try {
      const orig = { name: profile.name ?? "", bio: profile.bio ?? "" };
      const next = { name: form.name.trim(), bio: form.bio.trim() };
      const payload: Record<string, string> = {};
      (Object.keys(next) as (keyof typeof next)[]).forEach((k) => {
        if (next[k] !== orig[k]) payload[k] = next[k];
      });
      if (Object.keys(payload).length > 0) {
        await usersService.updateProfile(payload);
      }
      await refreshUser();
      setEditing(false);
      setStatusMsg("Profile saved.");
      onSaved();
    } catch (err: unknown) {
      const msg = (err as Error)?.message || "";
      setStatusMsg(msg || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Basic Info</h2>
          <p className="text-xs text-gray-500 mt-0.5">Your name and bio</p>
        </div>
        {!editing && (
          <button
            onClick={() => { setEditing(true); setStatusMsg(null); }}
            className="bg-blue-500 hover:bg-blue-400 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all"
          >
            Edit
          </button>
        )}
      </div>

      {statusMsg && (
        <p className={`text-sm px-4 py-2 rounded-xl ${statusMsg.startsWith("Failed") ? "bg-red-50 border border-red-200 text-red-600" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}>
          {statusMsg}
        </p>
      )}

      {/* Profile Photo — always visible */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-medium text-gray-500 mb-3">Profile Photo</p>
        <ProfilePhotoCard
          currentImageUrl={profile.profile_image}
          onSaved={onSaved}
        />
      </div>

      {editing ? (
        <form onSubmit={handleSave} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-gray-600 text-xs font-medium mb-1.5 block">Full name</label>
            <input
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              className="bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 w-full text-sm transition-all"
            />
          </div>
          <div>
            <label className="text-gray-600 text-xs font-medium mb-1.5 block">Email</label>
            <input
              type="email"
              value={profile.email ?? ""}
              disabled
              className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-500 w-full text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-gray-600 text-xs font-medium mb-1.5 block">Bio / About</label>
            <textarea
              name="bio"
              value={form.bio}
              onChange={handleChange}
              rows={3}
              className="bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 w-full text-sm transition-all resize-none"
              placeholder="Tell us about yourself…"
            />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={saving}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white rounded-xl px-6 py-2 text-sm font-semibold shadow-lg transition-all disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="border border-gray-300 text-gray-600 hover:bg-gray-100 rounded-xl px-4 py-2 text-sm transition-all">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {[
              ["Name", profile.name],
              ["Email", profile.email],
            ].map(([label, value]) => (
              <div key={label as string}>
                <span className="text-gray-500 text-xs">{label}</span>
                <p className="font-medium text-gray-900 mt-0.5">{value || <span className="text-gray-400 text-sm italic">Not set</span>}</p>
              </div>
            ))}
            {profile.bio && (
              <div className="md:col-span-2">
                <span className="text-gray-500 text-xs">Bio</span>
                <p className="text-gray-700 mt-0.5 text-sm leading-relaxed">{profile.bio}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact/location details — captured during onboarding, edited inline */}
      <div className="border-t border-gray-100 pt-4">
        <EditableOnboardingStepsCard
          profile={profile}
          role="buyer"
          title="Contact & Location"
          subtitle="Captured during onboarding — edit any step below"
          activeStepKey={onboardingStepKey}
          onActiveStepKeyChange={onOnboardingStepKeyChange}
          onSaved={onSaved}
        />
      </div>
    </section>
  );
}

// ── Seller profile section ────────────────────────────────────────────────────

// ── Change Password section ───────────────────────────────────────────────────

function ChangePasswordSection() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState<{ ok: boolean; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) { setMsg({ ok: false, text: "New passwords do not match." }); return; }
    if (next.length < 8)  { setMsg({ ok: false, text: "New password must be at least 8 characters." }); return; }
    setSaving(true);
    try {
      await authService.changePassword(current, next);
      setMsg({ ok: true, text: "Password changed successfully. Logging you out…" });
      setCurrent(""); setNext(""); setConfirm("");
      // All sessions are invalidated server-side on password change — log out locally and redirect.
      setTimeout(() => {
        logout();
        navigate("/login", { replace: true });
      }, 1500);
    } catch (err) {
      setMsg({ ok: false, text: parseApiError(err, "Failed to change password.").message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Change Password</h2>
      <p className="text-xs text-gray-500 mb-5">Update your login password</p>
      {msg && (
        <p className={`text-sm px-4 py-2 rounded-xl mb-4 ${msg.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-600"}`}>
          {msg.text}
        </p>
      )}
      <form onSubmit={(e) => void handleSubmit(e)} className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="text-gray-600 text-xs font-medium mb-1.5 block">Current password</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required
            className="bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 w-full text-sm transition-all" />
        </div>
        <div>
          <label className="text-gray-600 text-xs font-medium mb-1.5 block">New password</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8}
            className="bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 w-full text-sm transition-all" />
        </div>
        <div>
          <label className="text-gray-600 text-xs font-medium mb-1.5 block">Confirm new password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
            className="bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 w-full text-sm transition-all" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={saving || !current || !next || !confirm}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white rounded-xl px-6 py-2 text-sm font-semibold shadow-lg transition-all disabled:opacity-50">
            {saving ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </section>
  );
}

function SellerProfileSection({
  profile,
  score,
  onSaved,
  onboardingStepKey,
  onOnboardingStepKeyChange,
}: {
  profile: AuthUser;
  score: SellerScore | null;
  onSaved: () => void;
  onboardingStepKey?: string | null;
  onOnboardingStepKeyChange?: (key: string) => void;
}) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const missingFields = getMissingSellerOnboardingFields(profile);
  const missingFieldLabels = getMissingSellerOnboardingFieldLabels(profile);
  const isComplete = isSellerOnboardingComplete(profile);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(profile.name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [nameStatus, setNameStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!editingName) setNameValue(profile.name ?? "");
  }, [profile.name, editingName]);

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === (profile.name ?? "")) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    setNameStatus(null);
    try {
      await usersService.updateProfile({ name: trimmed });
      await refreshUser();
      setEditingName(false);
      onSaved();
    } catch (err: unknown) {
      const msg = (err as Error)?.message || "";
      setNameStatus(msg || "Failed to save name. Please try again.");
    } finally {
      setSavingName(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between px-6 pt-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Seller Profile</h2>
          <p className="text-xs text-gray-500 mt-0.5">Your agency details and market coverage</p>
        </div>
        {isComplete ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-700">
            &#10003; Complete
          </span>
        ) : (
          <button
            onClick={() => navigate(SELLER_ONBOARDING_PATH, { state: { from: "/profile" } })}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-all"
          >
            &#9888; Complete onboarding
          </button>
        )}
      </div>

      {/* Profile Photo */}
      <div className="px-6 pt-5">
        <p className="text-xs font-medium text-gray-500 mb-3">Profile Photo</p>
        <ProfilePhotoCard
          currentImageUrl={profile.profile_image}
          onSaved={onSaved}
        />
      </div>

      {/* Name — editable, defaults to the name entered at signup */}
      <div className="px-6 pt-5">
        <p className="text-xs font-medium text-gray-500 mb-2">Name</p>
        {nameStatus && (
          <p className="text-sm px-4 py-2 mb-2 rounded-xl bg-red-50 border border-red-200 text-red-600">
            {nameStatus}
          </p>
        )}
        {editingName ? (
          <form onSubmit={handleSaveName} className="flex items-center gap-2">
            <input
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              autoFocus
              className="bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 flex-1 text-sm transition-all"
            />
            <button
              type="submit"
              disabled={savingName}
              className="bg-blue-500 hover:bg-blue-400 text-white rounded-xl px-4 py-2.5 text-sm font-medium transition-all disabled:opacity-50"
            >
              {savingName ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => { setEditingName(false); setNameValue(profile.name ?? ""); setNameStatus(null); }}
              className="text-gray-500 hover:text-gray-700 text-sm px-2"
            >
              Cancel
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-3">
            <p className="text-gray-900 font-medium text-sm">{profile.name || "—"}</p>
            <button
              onClick={() => setEditingName(true)}
              className="text-blue-600 hover:text-blue-500 text-xs font-medium"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {/* Inventory score bar */}
      {score && score.active_deal_count > 0 && (
        <div className="px-6 pt-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">Inventory score</span>
            <span className="text-sm font-bold text-gray-900">{score.inventory_score.toFixed(0)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full h-full transition-all"
              style={{ width: `${Math.min(score.inventory_score, 100)}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="bg-gray-100 border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{score.active_deal_count} deals</span>
            {score.countries?.slice(0, 4).map((c) => (
              <span key={c} className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">{c}</span>
            ))}
            {(score.countries?.length ?? 0) > 4 && (
              <span className="text-gray-400">+{score.countries.length - 4} more</span>
            )}
          </div>
        </div>
      )}

      {/* Status messages */}
      {!isComplete && missingFields.length > 0 && (
        <div className="px-6 pt-5">
          <div className="bg-amber-50 border border-amber-300 text-amber-700 rounded-xl px-4 py-3 text-sm">
            Missing: {missingFieldLabels.join(", ")}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6 border-t border-gray-100">
        <EditableOnboardingStepsCard
          profile={profile}
          role="seller"
          title="Agency Details"
          subtitle="Contact, business, location and specialisation details captured during onboarding — edit any step below"
          activeStepKey={onboardingStepKey}
          onActiveStepKeyChange={onOnboardingStepKeyChange}
          onSaved={onSaved}
        />
      </div>
    </section>
  );
}

function InterestGraphSection({ graph }: { graph: InterestGraphData }) {
  const cats = graph.categories ?? {};
  const totalEntries = Object.keys(graph.destinations).length + Object.keys(graph.tags).length + Object.keys(graph.activities).length + Object.keys(cats).length;

  const sortedDest = Object.entries(graph.destinations).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const sortedTags = Object.entries(graph.tags).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const sortedActs = Object.entries(graph.activities).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const sortedCats = Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 10);

  const maxScore = Math.max(
    ...sortedDest.map(([, v]) => v),
    ...sortedTags.map(([, v]) => v),
    ...sortedActs.map(([, v]) => v),
    ...sortedCats.map(([, v]) => v),
    1,
  );

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Interest Graph</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {totalEntries > 0
              ? `${totalEntries} learned signals from your deal interactions`
              : "Start browsing deals to build your interest profile"}
          </p>
        </div>
        <span className="px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full border border-blue-200">
          Live
        </span>
      </div>

      {totalEntries === 0 ? (
        <p className="text-sm text-gray-400 italic">No signals yet &mdash; view, like, save or enquire about deals to populate this graph.</p>
      ) : (
        <div className="space-y-5">
          {sortedDest.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2.5">Destinations</h3>
              <div className="flex flex-wrap gap-2">
                {sortedDest.map(([dest, score]) => (
                  <div key={dest} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200">
                    <span className="text-xs text-blue-700 font-medium">{dest}</span>
                    <span className="text-xs text-blue-500">({score.toFixed(1)})</span>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(score / maxScore) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sortedTags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2.5">Travel Styles</h3>
              <div className="flex flex-wrap gap-2">
                {sortedTags.map(([tag, score]) => (
                  <div key={tag} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-50 border border-purple-200">
                    <span className="text-xs text-purple-700 font-medium">{tag}</span>
                    <span className="text-xs text-purple-500">({score.toFixed(1)})</span>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-400 rounded-full" style={{ width: `${(score / maxScore) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sortedActs.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2.5">Activities</h3>
              <div className="flex flex-wrap gap-2">
                {sortedActs.map(([act, score]) => (
                  <div key={act} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                    <span className="text-xs text-emerald-700 font-medium">{act}</span>
                    <span className="text-xs text-emerald-500">({score.toFixed(1)})</span>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${(score / maxScore) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sortedCats.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2.5">Deal Categories</h3>
              <div className="flex flex-wrap gap-2">
                {sortedCats.map(([cat, score]) => (
                  <div key={cat} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200">
                    <span className="text-xs text-amber-700 font-medium capitalize">{cat}</span>
                    <span className="text-xs text-amber-500">({score.toFixed(1)})</span>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(score / maxScore) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── Buyer profile section — tab-based (Profile / Preferences / Interests) ──────

function BuyerProfileSection({
  profile,
  buyerPrefs,
  interestGraph,
  loadingBuyerPrefs,
  isEditingBuyer,
  setIsEditingBuyer,
  onSavePrefs,
  onSaved,
  renderTags,
  activeTab,
  onTabChange,
  prefsStartStep,
  onboardingStepKey,
  onOnboardingStepKeyChange,
}: {
  profile: AuthUser;
  buyerPrefs: BuyerPrefs | null;
  interestGraph: InterestGraphData | null;
  loadingBuyerPrefs: boolean;
  isEditingBuyer: boolean;
  setIsEditingBuyer: (v: boolean) => void;
  onSavePrefs: (prefs: BuyerPrefs) => Promise<void> | void;
  onSaved: () => void;
  renderTags: (items: string[], colorClass?: string) => React.ReactNode;
  activeTab?: "profile" | "preferences" | "interests";
  onTabChange?: (t: "profile" | "preferences" | "interests") => void;
  prefsStartStep?: number;
  onboardingStepKey?: string | null;
  onOnboardingStepKeyChange?: (key: string) => void;
}) {
  const [tabInternal, setTabInternal] = useState<"profile" | "preferences" | "interests">("profile");
  const tab = activeTab ?? tabInternal;
  const setTab = (t: "profile" | "preferences" | "interests") => { setTabInternal(t); onTabChange?.(t); };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-6">
        <h2 className="text-lg font-semibold text-gray-900">Buyer Profile</h2>
        <p className="text-xs text-gray-500 mt-0.5">Your profile, travel preferences, and learned interests</p>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-100 px-6 overflow-x-auto mt-5">
        {(["profile", "preferences", "interests"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-all capitalize ${
              tab === t ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {/* ── Profile ── */}
        {tab === "profile" && (
          <BuyerBasicInfoSection
            profile={profile}
            onSaved={onSaved}
            onboardingStepKey={onboardingStepKey}
            onOnboardingStepKeyChange={onOnboardingStepKeyChange}
          />
        )}

        {/* ── Preferences ── */}
        {tab === "preferences" && (
          loadingBuyerPrefs ? (
            <div className="animate-pulse space-y-3">
              <div className="h-5 w-32 bg-gray-200 rounded-lg" />
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-6 w-20 bg-gray-200 rounded-full" />
                ))}
              </div>
            </div>
          ) : isEditingBuyer && buyerPrefs ? (
            <PreferencesEditor
              key={prefsStartStep}
              initial={buyerPrefs}
              onSave={onSavePrefs}
              onCancel={() => setIsEditingBuyer(false)}
              initialStep={prefsStartStep ?? 0}
            />
          ) : buyerPrefs ? (
            <>
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm font-semibold text-gray-900">Travel preferences</p>
                <button
                  onClick={() => setIsEditingBuyer(true)}
                  className="bg-blue-500 hover:bg-blue-400 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all"
                >
                  Edit
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Countries</h3>
                  {renderTags(buyerPrefs?.countries || [])}
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Cities</h3>
                  {renderTags(buyerPrefs?.cities || [])}
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Travel Styles</h3>
                  {renderTags(buyerPrefs?.tags || [], "bg-purple-500/20 text-purple-300 border-purple-500/30")}
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Activities</h3>
                  {renderTags(buyerPrefs?.activities || [], "bg-emerald-500/20 text-emerald-300 border-emerald-500/30")}
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Personality</h3>
                  {renderTags(buyerPrefs?.personality || [], "bg-indigo-50 text-indigo-700 border-indigo-200")}
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Budget Range</h3>
                  {buyerPrefs && buyerPrefs.budget_min !== null && buyerPrefs.budget_max !== null ? (
                    <span className="text-sm font-medium text-gray-900">
                      &#8377;{buyerPrefs.budget_min.toLocaleString()} &ndash; &#8377;{buyerPrefs.budget_max.toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Not set</span>
                  )}
                </div>
              </div>
              {buyerPrefs?.description && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">About</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{buyerPrefs.description}</p>
                </div>
              )}
            </>
          ) : null
        )}

        {/* ── Interests ── */}
        {tab === "interests" && (
          <InterestGraphSection graph={interestGraph || { destinations: {}, tags: {}, activities: {} }} />
        )}
      </div>
    </section>
  );
}

// ── Profile completeness card ─────────────────────────────────────────────────


interface CompletenessItem {
  key: string;
  label: string;
  impact: string;
  done: boolean;
  action: () => void;
}

function ProfileCompletenessCard({ items }: { items: CompletenessItem[] }) {
  const [collapsed, setCollapsed] = useState(false);
  const total = items.length;
  const done  = items.filter((i) => i.done).length;
  const pct   = Math.round((done / total) * 100);
  const incomplete = items.filter((i) => !i.done);

  if (done === total) return null;

  const color = pct >= 75 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-400" : "bg-blue-500";

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Profile completeness</h2>
          <p className="text-xs text-gray-500 mt-0.5">A complete profile gets better matches and more seller responses</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-gray-900">{pct}%</span>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            {collapsed ? "Show ▾" : "Hide ▴"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-2.5 mb-5">
            <div className={`${color} h-2.5 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
          </div>

          {/* Incomplete items with impact */}
          {incomplete.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Complete these to improve your profile</p>
              {incomplete.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={item.action}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition-all text-left group"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700">{item.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.impact}</p>
                  </div>
                  <span className="text-xs font-semibold text-blue-500 group-hover:text-blue-700 flex-shrink-0">Fix →</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const tier = useMyTier();
  const [profile, setProfile] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<ParsedApiError | null>(null);

  const [buyerPrefs, setBuyerPrefs] = useState<BuyerPrefs | null>(null);
  const [interestGraph, setInterestGraph] = useState<InterestGraphData | null>(null);
  const [isEditingBuyer, setIsEditingBuyer] = useState(false);
  const [buyerPrefsStatus, setBuyerPrefsStatus] = useState<string | null>(null);
  const [loadingBuyerPrefs, setLoadingBuyerPrefs] = useState(false);

  // Lifted tab states so completeness card can jump to the right section
  const [buyerTab,       setBuyerTab]       = useState<"profile" | "preferences" | "interests">("profile");
  const [prefsStartStep] = useState(0);
  // Which onboarding-step tab is active inside the inline editable steps card —
  // driven by the completeness card's "Fix" action or the step's own tab click.
  const [onboardingStepKey, setOnboardingStepKey] = useState<string | null>(null);

  const [sellerScore, setSellerScore] = useState<SellerScore | null>(null);

  // Deep-link target from the "Complete your profile" verification prompt
  // (?step=company_details — see VerificationRequiredModal) — jumps the
  // inline editable-steps card straight to the step in question.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const requestedStep = searchParams.get("step");
    if (!requestedStep) return;
    setOnboardingStepKey(requestedStep);
    const next = new URLSearchParams(searchParams);
    next.delete("step");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const loadData = async () => {
    setLoading(true);
    setError(null);
    setErrorInfo(null);
    try {
      const me = await usersService.getCurrentProfile();
      setProfile(me);
    } catch (err: any) {
      const parsed = parseApiError(err, "Failed to load profile data.");
      setError(parsed.message);
      setErrorInfo(parsed);
    } finally {
      setLoading(false);
    }
  };

  const loadBuyerPreferences = async () => {
    setLoadingBuyerPrefs(true);
    try {
      const [prefs, graph] = await Promise.all([
        recommendationsService.getBuyerPreferences(),
        recommendationsService.getBuyerInterestGraph(),
      ]);
      setBuyerPrefs({
        ...prefs,
        travel_style: (profile as any)?.travel_style ?? [],
        personality:  (profile as any)?.personality  ?? [],
        description:  (profile as any)?.bio           ?? "",
      });
      setInterestGraph(graph as InterestGraphData);
    } catch {
      setBuyerPrefs({ countries: [], cities: [], tags: [], activities: [], travel_style: (profile as any)?.travel_style ?? [], personality: (profile as any)?.personality ?? [], description: (profile as any)?.bio ?? "", budget_min: null, budget_max: null });
      setInterestGraph({ destinations: {}, tags: {}, activities: {} });
    } finally {
      setLoadingBuyerPrefs(false);
    }
  };


  useEffect(() => {
    void loadData();
  }, []);

  // After profile loads, redirect incomplete sellers to onboarding
  useEffect(() => {
    if (!profile) return;
    
    // If the user was sent here specifically to fix a missing field (e.g. from Publish),
    // let them stay on the profile page to fix it instead of forcing them back into the onboarding flow.
    if (location.state && (location.state as any).resumeStepKey) return;

    if (isSellerRole(profile.role) && !isSellerOnboardingComplete(profile)) {
      navigate(SELLER_ONBOARDING_PATH, { replace: true, state: { from: location.pathname } });
    }
  }, [profile, navigate, location.pathname, location.state]);

  // Handle resumeStepKey from navigation state (e.g. from MyInventoryPage)
  useEffect(() => {
    if (profile && location.state && (location.state as any).resumeStepKey) {
      const stepKey = (location.state as any).resumeStepKey;
      if (isSellerRole(profile.role)) {
        setOnboardingStepKey(stepKey);
      } else {
        setBuyerTab("profile");
        setOnboardingStepKey(stepKey);
      }
      // Clear the state so it doesn't trigger again on reload
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [profile, location.state, location.pathname, navigate]);

  // Load role-appropriate recommendation context
  useEffect(() => {
    if (!profile?.role) return;
    if (isBuyerRole(profile.role)) {
      void loadBuyerPreferences();
    } else if (isSellerRole(profile.role)) {
      // Inventory score: read from profile first, then fetch full breakdown
      const _profileScore = (profile as any).inventory_score ?? 0;
      const _profileDeals = (profile as any).active_deal_count ?? 0;
      setSellerScore({
        seller_id: profile.id,
        inventory_score: _profileScore,
        active_deal_count: _profileDeals,
        countries: [], cities: [], tags: [], activities: [],
        price_min: 0, price_max: 0, last_recalculated: "",
      });
      recommendationsService.getSellerScore(profile.id).then((s) => {
        setSellerScore(s);
      }).catch(() => { /* keep profile-level score */ });

    }
  // Re-run whenever profile changes so saved preferences show immediately
  }, [profile]);



  const showBuyerPrefs = isBuyerRole(profile?.role);

  // ── Completeness items ────────────────────────────────────────────────────


  const SELLER_STEP_IMPACT: Record<string, string> = {
    phone:        "Buyers can contact you directly via phone or WhatsApp",
    business:     "Builds trust — buyers verify your agency before enquiring",
    seller_type:  "Buyers find you in the right deal category",
    address:      "Enables location-based matching and local credibility",
    preferences:  "Powers market recommendations and deal-to-seller matching",
    travel_style: "Helps buyers understand the experiences you specialise in",
    personality:  "Builds stronger buyer connections and personal branding",
  };

  const completenessItems: CompletenessItem[] = profile
    ? isSellerRole(profile.role)
      ? (profile.onboarding?.steps ?? [])
          .filter((s) => !s.skipped)
          .map((s) => ({
            key:    s.key,
            label:  s.label,
            impact: SELLER_STEP_IMPACT[s.key] ?? "Complete this to improve your profile",
            done:   s.completed,
            action: () => {
              setOnboardingStepKey(s.key);
            },
          }))
      : (profile.onboarding?.steps ?? [])
          .filter((s) => !s.skipped)
          .map((s) => ({
            key:    s.key,
            label:  s.label,
            impact: "Complete this to improve your profile and get better matches",
            done:   s.completed,
            action: () => {
              // Switch to the Profile tab and this step's tab in-page — no navigation.
              setBuyerTab("profile");
              setOnboardingStepKey(s.key);
            },
          }))
    : [];

  const saveBuyerPrefs = async (prefs: BuyerPrefs) => {
    setBuyerPrefsStatus(null);
    try {
      await recommendationsService.saveBuyerPreferences({
        countries: prefs.countries,
        cities: prefs.cities,
        tags: prefs.tags,
        activities: prefs.activities,
        budget_min: prefs.budget_min,
        budget_max: prefs.budget_max,
      });
      // travel_style, personality, bio stored on user profile
      const profileUpdate: Record<string, any> = { travel_style: prefs.travel_style };
      if (prefs.personality?.length) profileUpdate.personality = prefs.personality;
      if (prefs.description?.trim()) profileUpdate.bio = prefs.description.trim();
      await usersService.updateProfile(profileUpdate);
      setIsEditingBuyer(false);
      setBuyerPrefsStatus("Preferences updated successfully.");
      await loadData();
      void loadBuyerPreferences();
    } catch (err) {
      setBuyerPrefsStatus("Failed to save preferences.");
    }
  };


  const renderBuyerPrefsTags = (items: string[], colorClass: string = "bg-blue-50 text-blue-700 border-blue-200") => (
    <div className="flex flex-wrap gap-1.5">
      {items.length === 0 ? <span className="text-sm text-gray-400 italic">None selected</span> : items.map((item) => (
        <span key={item} className={`px-2 py-0.5 rounded-full text-xs border ${colorClass}`}>{item}</span>
      ))}
    </div>
  );

  // Derive initials for hero avatar fallback
  const initials = profile?.name
    ? profile.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
            <TierBadge tier={tier} />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/subscription")}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-100 transition-all text-sm font-medium"
            >
              Manage plan
            </button>
            <button
              onClick={logout}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-100 transition-all text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {error ? (
          errorInfo?.isSellerOnboardingIncomplete ? (
            <SellerOnboardingAlert message={errorInfo.message} missingFields={errorInfo.missingFields} missingFieldsDetailed={errorInfo.missingFieldsDetailed} returnTo={location.pathname} />
          ) : (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2 text-sm">{error}</div>
          )
        ) : null}

        {/* Hero banner — merges "Current User" section */}
        <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 rounded-2xl p-8 relative overflow-hidden shadow-lg">
          {/* Decorative background circles */}
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-white opacity-5 pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-80 h-80 rounded-full bg-white opacity-5 pointer-events-none" />
          <div className="absolute top-1/2 right-1/3 w-40 h-40 rounded-full bg-white opacity-5 pointer-events-none" />

          {loading && !profile ? (
            <div className="animate-pulse space-y-4 relative z-10">
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 rounded-full bg-blue-400/30" />
                <div className="space-y-2">
                  <div className="h-6 w-40 bg-blue-400/30 rounded-lg" />
                  <div className="h-4 w-56 bg-blue-400/30 rounded-lg" />
                  <div className="h-5 w-20 bg-blue-400/30 rounded-full" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/30">
                <div className="h-10 bg-blue-400/30 rounded-lg" />
                <div className="h-10 bg-blue-400/30 rounded-lg" />
              </div>
            </div>
          ) : profile ? (
            <div className="relative z-10">
              <div className="flex items-center gap-5 mb-6">
                {/* Avatar */}
                <div className="w-20 h-20 rounded-full border-2 border-white/40 bg-white/20 overflow-hidden flex items-center justify-center flex-shrink-0 shadow-lg">
                  {profile.profile_image ? (
                    <img src={profile.profile_image} alt={profile.name ?? "Profile"} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-gray-900">{initials}</span>
                  )}
                </div>
                {/* Name + email + role */}
                <div className="flex flex-col gap-1.5">
                  <h2 className="text-2xl font-bold text-white leading-tight">{profile.name || "—"}</h2>
                  <p className="text-white/70 text-sm">{(profile as any).email || "—"}</p>
                  {profile.role && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold w-fit ${
                      isSellerRole(profile.role)
                        ? "bg-blue-500/40 text-blue-100 border border-blue-300/30"
                        : "bg-emerald-500/40 text-emerald-100 border border-emerald-300/30"
                    }`}>
                      {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                    </span>
                  )}
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-5 border-t border-white/30">
                <div>
                  <p className="text-white/50 text-xs mb-0.5">Role</p>
                  <p className="text-gray-800 font-medium text-sm">{profile.role || "—"}</p>
                </div>
                <div>
                  <p className="text-white/50 text-xs mb-0.5">User ID</p>
                  <p className="text-white font-mono text-xs break-all leading-snug">{profile.id || "—"}</p>
                </div>
                <div>
                  <p className="text-white/50 text-xs mb-0.5">Name</p>
                  <p className="text-gray-800 font-medium text-sm">{profile.name || "—"}</p>
                </div>
                <div>
                  <p className="text-white/50 text-xs mb-0.5">Email</p>
                  <p className="text-gray-800 font-medium text-sm truncate">{(profile as any).email || "—"}</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>


        {/* Profile completeness card */}
        {profile && completenessItems.length > 0 && (
          <ProfileCompletenessCard items={completenessItems} />
        )}

        {profile && isSellerRole(profile.role) ? (
          <div id="seller-profile-section">
            <SellerProfileSection
              profile={profile}
              score={sellerScore}
              onSaved={() => void loadData()}
              onboardingStepKey={onboardingStepKey}
              onOnboardingStepKeyChange={setOnboardingStepKey}
            />
          </div>
        ) : null}

        {showBuyerPrefs && profile ? (
          <div id="buyer-profile-section">
            {buyerPrefsStatus ? (
              <div className={`text-sm px-4 py-2 rounded-xl ${buyerPrefsStatus.startsWith("Failed") ? "bg-red-50 border border-red-200 text-red-600" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}>
                {buyerPrefsStatus}
              </div>
            ) : null}

            <BuyerProfileSection
              profile={profile}
              buyerPrefs={buyerPrefs}
              interestGraph={interestGraph}
              loadingBuyerPrefs={loadingBuyerPrefs}
              isEditingBuyer={isEditingBuyer}
              setIsEditingBuyer={setIsEditingBuyer}
              onSavePrefs={saveBuyerPrefs}
              onSaved={() => void loadData()}
              renderTags={renderBuyerPrefsTags}
              activeTab={buyerTab}
              onTabChange={setBuyerTab}
              prefsStartStep={prefsStartStep}
              onboardingStepKey={onboardingStepKey}
              onOnboardingStepKeyChange={setOnboardingStepKey}
            />
          </div>
        ) : null}

        {/* Change Password — visible for all roles */}
        {profile && <ChangePasswordSection />}

      </main>
    </div>
  );
};

export default ProfilePage;
